import React from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Instagram,
  Linkedin,
  Youtube,
  Facebook,
  Send,
  ArrowRight,
} from "lucide-react";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const quickLinks = [
    { name: "Beranda", section: "hero" },
    { name: "Tentang Kami", section: "about" },
    { name: "Layanan", section: "services" },
    { name: "Portfolio", section: "portfolio" },
    { name: "Tim", section: "team" },
    { name: "Testimoni", section: "testimonials" },
  ];

  const services = [
    "Videography",
    "Animation 2D/3D",
    "Digital Advertising",
    "Multimedia Development",
    "Brand Strategy",
    "Website & Mobile Apps",
  ];

  const socialLinks = [
    { icon: Instagram, href: "#", name: "Instagram" },
    { icon: Linkedin, href: "#", name: "LinkedIn" },
    { icon: Youtube, href: "#", name: "YouTube" },
    { icon: Facebook, href: "#", name: "Facebook" },
  ];

  return (
    <footer
      id="contact"
      className="bg-gray-900 text-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.1%22%3E%3Cpath d=%22M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z%22/%3E%3C/g%3E%3C/svg%3E')",
          }}
        ></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://ranselpro.com/tim/logo-ranselpro.png"
                  alt="RanselPro Logo"
                  className="w-10 h-10 rounded-md object-contain"
                />
                <div>
                  <h3 className="text-2xl font-bold">RanselPro</h3>
                  <p className="text-gray-400 text-sm">Multimedia Solutions</p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                PT Multimedia Ransel Pro adalah partner terpercaya untuk solusi
                multimedia, branding, dan pengembangan digital yang inovatif.
              </p>

              {/* Social Media Links */}
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 group"
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xl font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.section)}
                      className="text-gray-300 hover:text-blue-400 transition-colors duration-200 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-xl font-bold mb-6">Layanan Kami</h4>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li
                    key={index}
                    className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
                  >
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-xl font-bold mb-6">Kontak Kami</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mt-1">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-white mb-1">Alamat</h5>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Treasury Tower Office
                      <br />
                      District 8, SCBD Lot.28
                      <br />
                      Senayan, Jakarta 12190
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-white mb-1">Telepon</h5>
                    <p className="text-gray-300">+62 895-2285-1749</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-white mb-1">Email</h5>
                    <p className="text-gray-300">info@ranselpro.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-white mb-1">Jam Kerja</h5>
                    <p className="text-gray-300 text-sm">
                      Senin - Jumat: 09:00 - 18:00
                      <br />
                      Sabtu: 09:00 - 15:00
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="py-8 border-t border-gray-800">
          <Card className="bg-gradient-to-r from-blue-600/20 to-orange-600/20 border-gray-700 backdrop-blur-sm">
            <div className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Dapatkan Update Terbaru
                  </h3>
                  <p className="text-gray-300">
                    Berlangganan newsletter untuk mendapatkan tips, trend, dan
                    project showcase terbaru
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                  <div className="relative">
                    <input
                      type="email"
                      placeholder="Masukkan email Anda"
                      className="bg-gray-800 border border-gray-600 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-full md:w-80"
                    />
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold whitespace-nowrap">
                    <Send className="w-5 h-5 mr-2" />
                    Subscribe
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Bottom Footer */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-gray-400 text-center md:text-left">
              <p>&copy; 2024 PT Multimedia Ransel Pro. All rights reserved.</p>
              <p className="text-sm">Crafted with ❤️ for creative excellence</p>
            </div>

            <div className="flex items-center space-x-6">
              <button
                onClick={scrollToTop}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
              >
                Kembali ke Atas
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
